import { useState } from 'react';
import { Link } from 'react-router-dom';
import { errorMessage } from '../api/client';
import { partsApi, type ItemForm, type ReferenceForm } from '../api/endpoints';
import type { PartItem, PartReference } from '../api/types';
import { Badge } from '../components/Badge';
import { Field } from '../components/Field';
import { Modal } from '../components/Modal';
import { Notice } from '../components/Notice';
import { StockRegularisationModal } from '../components/StockRegularisationModal';
import {
  PART_CATEGORIES,
  PART_ITEM_STATES,
  PART_TRACKINGS,
  PART_TRACKING_HINTS,
  PART_TRACKING_LABELS,
  PART_ITEM_STATE_TONES,
  partCategoryLabel,
  partItemStateLabel,
  stockTone,
  type PartCategory,
  type PartItemState,
  type PartTracking,
} from '../domain/parts';
import { canWrite } from '../domain/roles';
import { useAsync } from '../hooks/useAsync';
import { formatDateTime } from '../lib/format';
import { useLiveEvents } from '../realtime/RealtimeContext';
import { useCurrentUser } from '../session/SessionContext';

function emptyReference(): ReferenceForm {
  return {
    designation: '',
    category: 'memoire',
    tracking: 'quantite',
    brand: '',
    model: '',
    note: '',
    declaredQuantity: 0,
  };
}

function ReferenceModal({
  reference,
  onClose,
  onSaved,
}: {
  reference: PartReference | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState<ReferenceForm>(() =>
    reference === null
      ? emptyReference()
      : {
          designation: reference.designation,
          category: reference.category,
          tracking: reference.tracking,
          brand: reference.brand,
          model: reference.model,
          note: reference.note,
          declaredQuantity: reference.declaredQuantity,
        },
  );
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const patch = (change: Partial<ReferenceForm>): void =>
    setForm((current) => ({ ...current, ...change }));

  const suiviFige = reference !== null && !reference.trackingEditable;

  const onSubmit = (event: React.FormEvent): void => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    const requete =
      reference === null
        ? partsApi.createReference(form)
        : partsApi.updateReference(reference.id, form);
    requete
      .then(() => onSaved())
      .catch((cause: unknown) => setError(errorMessage(cause)))
      .finally(() => setBusy(false));
  };

  return (
    <Modal
      title={reference === null ? 'Nouvelle référence' : `Modifier ${reference.designation}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="bouton" onClick={onClose}>
            Annuler
          </button>
          <button
            type="submit"
            form="formulaire-reference"
            className="bouton bouton--primaire"
            disabled={busy}
          >
            {busy ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </>
      }
    >
      <form id="formulaire-reference" className="pile pile--serree" onSubmit={onSubmit}>
        {error !== null && <Notice tone="erreur">{error}</Notice>}
        {suiviFige && (
          <Notice tone="info">
            Des mouvements concernent déjà cette référence : son mode de suivi ne peut
            plus être modifié.
          </Notice>
        )}

        <div className="grille-champs">
          <Field label="Désignation">
            <input
              value={form.designation}
              required
              maxLength={200}
              onChange={(event) => patch({ designation: event.target.value })}
            />
          </Field>

          <Field label="Catégorie">
            <select
              value={form.category}
              onChange={(event) =>
                patch({ category: event.target.value as PartCategory })
              }
            >
              {PART_CATEGORIES.map((category) => (
                <option key={category} value={category}>
                  {partCategoryLabel(category)}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Mode de suivi" hint={PART_TRACKING_HINTS[form.tracking]}>
            <select
              value={form.tracking}
              disabled={suiviFige}
              onChange={(event) => {
                const tracking = event.target.value as PartTracking;
                // une référence suivie à l'exemplaire n'a pas de quantité déclarée
                patch({
                  tracking,
                  declaredQuantity: tracking === 'quantite' ? form.declaredQuantity : 0,
                });
              }}
            >
              {PART_TRACKINGS.map((tracking) => (
                <option key={tracking} value={tracking}>
                  {PART_TRACKING_LABELS[tracking]}
                </option>
              ))}
            </select>
          </Field>

          {form.tracking === 'quantite' && (
            <Field
              label="Stock déclaré"
              hint="Le stock courant s'en déduit par les mouvements du journal."
            >
              <input
                type="number"
                min={0}
                value={form.declaredQuantity}
                onChange={(event) =>
                  patch({ declaredQuantity: Number.parseInt(event.target.value, 10) || 0 })
                }
              />
            </Field>
          )}

          <Field label="Marque">
            <input
              value={form.brand}
              maxLength={120}
              onChange={(event) => patch({ brand: event.target.value })}
            />
          </Field>

          <Field label="Modèle">
            <input
              value={form.model}
              maxLength={120}
              onChange={(event) => patch({ model: event.target.value })}
            />
          </Field>

          <Field label="Note" wide>
            <textarea
              value={form.note}
              maxLength={4000}
              onChange={(event) => patch({ note: event.target.value })}
            />
          </Field>
        </div>
      </form>
    </Modal>
  );
}

function ItemsModal({
  reference,
  onClose,
  onSaved,
}: {
  reference: PartReference;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [lignes, setLignes] = useState('');
  const [state, setState] = useState<PartItemState>('neuf');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const numeros = lignes
    .split('\n')
    .map((ligne) => ligne.trim())
    .filter((ligne) => ligne !== '');

  const onSubmit = (event: React.FormEvent): void => {
    event.preventDefault();
    const items: ItemForm[] =
      numeros.length === 0 ? [{ serialNumber: null, state }] : numeros.map((serialNumber) => ({ serialNumber, state }));

    setBusy(true);
    setError(null);
    partsApi
      .createItems(reference.id, items)
      .then(() => onSaved())
      .catch((cause: unknown) => setError(errorMessage(cause)))
      .finally(() => setBusy(false));
  };

  return (
    <Modal
      title={`Exemplaires de ${reference.designation}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="bouton" onClick={onClose}>
            Annuler
          </button>
          <button
            type="submit"
            form="formulaire-exemplaires"
            className="bouton bouton--primaire"
            disabled={busy}
          >
            {busy
              ? 'Création…'
              : `Créer ${numeros.length === 0 ? 'un exemplaire' : `${numeros.length} exemplaire(s)`}`}
          </button>
        </>
      }
    >
      <form id="formulaire-exemplaires" className="pile pile--serree" onSubmit={onSubmit}>
        {error !== null && <Notice tone="erreur">{error}</Notice>}
        <div className="grille-champs">
          <Field label="État à la création">
            <select
              value={state}
              onChange={(event) => setState(event.target.value as PartItemState)}
            >
              {PART_ITEM_STATES.map((valeur) => (
                <option key={valeur} value={valeur}>
                  {partItemStateLabel(valeur)}
                </option>
              ))}
            </select>
          </Field>

          <Field
            label="Numéros de série"
            wide
            hint="Un par ligne. Laisser vide pour créer un exemplaire sans numéro."
          >
            <textarea
              value={lignes}
              onChange={(event) => setLignes(event.target.value)}
              placeholder={'SN-0001\nSN-0002'}
            />
          </Field>
        </div>
      </form>
    </Modal>
  );
}

/** Historique de localisation : la vraie valeur du suivi à l'exemplaire. */
function ItemHistoryModal({ item, onClose }: { item: PartItem; onClose: () => void }) {
  const historique = useAsync((signal) => partsApi.itemHistory(item.id, signal), [item.id]);

  return (
    <Modal
      title={`Historique — ${item.serialNumber ?? `exemplaire ${item.id.slice(0, 8)}`}`}
      onClose={onClose}
    >
      <div className="pile pile--serree">
        {historique.error !== null && <Notice tone="erreur">{historique.error}</Notice>}
        {historique.loading && historique.data === null && (
          <div className="chargement">Chargement…</div>
        )}
        {historique.data !== null && historique.data.history.length === 0 && (
          <p className="doux">
            Cet exemplaire n'a jamais été monté : il est au stock depuis sa création.
          </p>
        )}
        {historique.data !== null && historique.data.history.length > 0 && (
          <ol className="frise">
            {historique.data.history.map((entree) => (
              <li key={entree.eventId} className="frise__entree">
                <div className="frise__jalon" aria-hidden="true" />
                <div className="frise__corps">
                  <div className="frise__titre">
                    <Badge tone={entree.direction === 'montee' ? 'ok' : 'neutre'}>
                      {entree.direction === 'montee' ? 'Monté' : 'Retiré'}
                    </Badge>
                    <Link to={`/postes/${entree.workstationId}`}>
                      {entree.workstationName}
                    </Link>
                    <span className="mono doux">{formatDateTime(entree.occurredAt)}</span>
                  </div>
                  <div className="frise__meta">Par {entree.author}</div>
                  {entree.reason !== '' && (
                    <div className="frise__motif">{entree.reason}</div>
                  )}
                </div>
              </li>
            ))}
          </ol>
        )}
      </div>
    </Modal>
  );
}

export function InventoryPage() {
  const user = useCurrentUser();
  const writable = canWrite(user.role);

  const [search, setSearch] = useState('');
  const [category, setCategory] = useState<PartCategory | ''>('');
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<PartReference | null>(null);
  const [addingItemsTo, setAddingItemsTo] = useState<PartReference | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [historyOf, setHistoryOf] = useState<PartItem | null>(null);
  const [regularising, setRegularising] = useState<PartReference | null>(null);

  const references = useAsync(
    (signal) =>
      partsApi.listReferences(
        {
          recherche: search === '' ? undefined : search,
          categorie: category === '' ? undefined : category,
        },
        signal,
      ),
    [search, category],
  );

  const detail = useAsync(
    (signal) =>
      expanded === null
        ? Promise.resolve(null)
        : partsApi.getReference(expanded, signal),
    [expanded],
  );

  // Le stock et l'emplacement des exemplaires se calculent depuis le journal :
  // seul un changement de pièce les fait bouger.
  useLiveEvents((event) => {
    if (event.type !== 'changementPiece') return;
    references.reload();
    detail.reload();
  });

  const liste = references.data?.references ?? [];
  const anomalies = liste.filter((reference) => reference.anomaly);

  return (
    <div className="pile">
      <div className="entete-page">
        <div>
          <h1>Inventaire</h1>
          <p className="entete-page__sous-titre">
            Le stock et l'emplacement de chaque exemplaire sont calculés depuis le
            journal : ils ne sont jamais saisis directement. Le stock compte les
            exemplaires <strong>disponibles</strong> — un exemplaire retiré ou
            défectueux est présent mais indisponible.
          </p>
        </div>
        {writable && (
          <button
            type="button"
            className="bouton bouton--primaire"
            onClick={() => setCreating(true)}
          >
            Nouvelle référence
          </button>
        )}
      </div>

      {anomalies.length > 0 && (
        <Notice tone="alerte" title="Stock négatif à régler">
          {anomalies.length === 1
            ? 'Une référence a un stock négatif : '
            : `${anomalies.length} références ont un stock négatif : `}
          {anomalies.map((reference) => reference.designation).join(', ')}. Plus de pièces
          ont été montées que le stock déclaré n'en comptait.
          <div style={{ marginTop: 'var(--espace-3)' }}>
            Trois façons d'en sortir, et la dernière est en général la bonne : annuler
            le mouvement en trop s'il n'a jamais eu lieu, corriger la quantité déclarée
            si c'est elle qui était fausse, ou <strong>régulariser après inventaire
            physique</strong> — ce qui n'efface rien et laisse une trace datée de ce
            qui a été compté.
          </div>
        </Notice>
      )}

      <div className="outils">
        <Field label="Recherche" hint="Désignation, marque, modèle.">
          <input
            className="champ--recherche"
            value={search}
            placeholder="Rechercher une référence…"
            onChange={(event) => setSearch(event.target.value)}
          />
        </Field>
        <Field label="Catégorie">
          <select
            value={category}
            onChange={(event) => setCategory(event.target.value as PartCategory | '')}
          >
            <option value="">Toutes</option>
            {PART_CATEGORIES.map((valeur) => (
              <option key={valeur} value={valeur}>
                {partCategoryLabel(valeur)}
              </option>
            ))}
          </select>
        </Field>
      </div>

      {references.error !== null && (
        <Notice tone="erreur" title="Chargement impossible">
          {references.error}
        </Notice>
      )}

      <div className="panneau">
        <div className="tableau-cadre">
          <table className="tableau">
            <thead>
              <tr>
                <th>Désignation</th>
                <th>Catégorie</th>
                <th>Suivi</th>
                <th>Stock</th>
                <th>Marque et modèle</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {references.loading && references.data === null && (
                <tr>
                  <td colSpan={6} className="tableau__vide">
                    Chargement…
                  </td>
                </tr>
              )}
              {liste.length === 0 && !references.loading && (
                <tr>
                  <td colSpan={6} className="tableau__vide">
                    Aucune référence ne correspond à ces critères.
                  </td>
                </tr>
              )}
              {liste.map((reference) => (
                <tr key={reference.id}>
                  <td>
                    <button
                      type="button"
                      className="tri"
                      onClick={() =>
                        setExpanded(expanded === reference.id ? null : reference.id)
                      }
                    >
                      {reference.designation}
                    </button>
                    {reference.tracking === 'exemplaire' && (
                      <div className="faible">{reference.itemCount} exemplaire(s)</div>
                    )}
                  </td>
                  <td>{partCategoryLabel(reference.category)}</td>
                  <td>{PART_TRACKING_LABELS[reference.tracking]}</td>
                  <td>
                    <Badge tone={stockTone(reference.stock)}>{reference.stock}</Badge>
                    {reference.tracking === 'quantite' && (
                      <div className="faible">déclaré {reference.declaredQuantity}</div>
                    )}
                    {/*
                      Un exemplaire retiré ou défectueux ne compte pas comme
                      stock, mais reste affiché : sans cela on met au rebut sans
                      le savoir.
                    */}
                    {reference.unavailableCount > 0 && (
                      <div className="faible">
                        dont indisponibles :{' '}
                        {Object.entries(reference.unavailable)
                          .map(([state, nombre]) => `${nombre} ${partItemStateLabel(state).toLowerCase()}`)
                          .join(', ')}
                      </div>
                    )}
                  </td>
                  <td>
                    {reference.brand} {reference.model}
                  </td>
                  <td>
                    {writable && (
                      <div className="ligne">
                        <button
                          type="button"
                          className="bouton bouton--petit"
                          onClick={() => setEditing(reference)}
                        >
                          Modifier
                        </button>
                        {reference.tracking === 'exemplaire' && (
                          <button
                            type="button"
                            className="bouton bouton--petit"
                            onClick={() => setAddingItemsTo(reference)}
                          >
                            Exemplaires
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {expanded !== null && (
        <section className="panneau">
          <div className="panneau__entete">
            <span className="panneau__titre">Exemplaires</span>
            {detail.data !== null && writable && (
              <button
                type="button"
                className="bouton bouton--petit pousse"
                onClick={() => setRegularising(detail.data!.reference)}
              >
                Régulariser après inventaire
              </button>
            )}
            <button
              type="button"
              className="bouton bouton--fantome bouton--petit"
              onClick={() => setExpanded(null)}
            >
              Fermer
            </button>
          </div>
          <div className="panneau__corps panneau__corps--nu">
            {detail.error !== null && (
              <div style={{ padding: 'var(--espace-5)' }}>
                <Notice tone="erreur">{detail.error}</Notice>
              </div>
            )}
            {detail.data !== null && detail.data.items.length === 0 && (
              <div className="tableau__vide">
                {detail.data.reference.tracking === 'quantite'
                  ? 'Cette référence est suivie à la quantité : elle ne porte pas d’exemplaires.'
                  : 'Aucun exemplaire déclaré pour cette référence.'}
              </div>
            )}
            {detail.data !== null && detail.data.items.length > 0 && (
              <div className="tableau-cadre">
                <table className="tableau">
                  <thead>
                    <tr>
                      <th>Numéro de série</th>
                      <th>État</th>
                      <th>Emplacement</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {detail.data.items.map((item) => (
                      <tr key={item.id}>
                        <td className="mono">
                          {item.serialNumber ?? `— ${item.id.slice(0, 8)}`}
                        </td>
                        <td>
                          <Badge tone={PART_ITEM_STATE_TONES[item.state]}>
                            {partItemStateLabel(item.state)}
                          </Badge>
                        </td>
                        <td>
                          {item.workstationId === null ? (
                            <span className="faible">Au stock</span>
                          ) : (
                            <Link to={`/postes/${item.workstationId}`}>
                              {item.workstationName}
                            </Link>
                          )}
                        </td>
                        <td>
                          <button
                            type="button"
                            className="bouton bouton--petit"
                            onClick={() => setHistoryOf(item)}
                          >
                            Historique
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </section>
      )}

      {(creating || editing !== null) && (
        <ReferenceModal
          reference={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={() => {
            setCreating(false);
            setEditing(null);
            references.reload();
            detail.reload();
          }}
        />
      )}

      {addingItemsTo !== null && (
        <ItemsModal
          reference={addingItemsTo}
          onClose={() => setAddingItemsTo(null)}
          onSaved={() => {
            setAddingItemsTo(null);
            references.reload();
            detail.reload();
          }}
        />
      )}

      {historyOf !== null && (
        <ItemHistoryModal item={historyOf} onClose={() => setHistoryOf(null)} />
      )}

      {regularising !== null && (
        <StockRegularisationModal
          reference={regularising}
          items={detail.data?.items ?? []}
          onClose={() => setRegularising(null)}
          onSaved={() => {
            setRegularising(null);
            references.reload();
            detail.reload();
          }}
        />
      )}
    </div>
  );
}
